import React from 'react';
import { motion } from 'framer-motion';
import { Workflow, Database, Repeat, Zap } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, RuleLabel,
  TextLink, BigStat, ClosingCta,
} from '../components/V2Shared';
import { ARIA_LOOP, ARIA_EXAMPLE } from '../data/site';

const CAPABILITIES = [
  {
    icon: Workflow,
    title: '100+ skills',
    body: 'Insurance, claims, scheduling, outreach, fax parsing, call handling, chart updates — one engine that can reach every corner of the practice.',
  },
  {
    icon: Database,
    title: 'Persistent memory',
    body: 'Context never dies between agents or between days. The conversation from three weeks ago informs the call ARIA makes today.',
  },
  {
    icon: Repeat,
    title: 'Multi-step reasoning',
    body: 'ARIA plans, acts, evaluates, and re-plans — running as many loops as the job takes, and challenging its own conclusions along the way.',
  },
  {
    icon: Zap,
    title: 'Real action',
    body: 'Not simulation, not suggestions. Calls placed, faxes parsed, charts updated, claims submitted — actual work in actual systems.',
  },
];

/* animated loop diagram */
const LoopDiagram = () => (
  <div className="v2-cross relative mx-auto w-full max-w-md">
    <svg viewBox="0 0 400 400" className="w-full">
      {/* orbit ring */}
      <circle cx="200" cy="200" r="150" fill="none" stroke="#dde5f3" strokeWidth="1.5" />
      <circle
        cx="200" cy="200" r="150" fill="none" stroke="#1a46e5" strokeWidth="2"
        strokeDasharray="10 14" strokeLinecap="round" className="v2-dash"
      />
      {/* core */}
      <circle cx="200" cy="200" r="64" fill="#0d2391" />
      <circle cx="200" cy="200" r="64" fill="none" stroke="#1a46e5" strokeWidth="1.5" opacity="0.5" />
      <text x="200" y="194" textAnchor="middle" fill="#ffffff" fontFamily="inherit" fontSize="26" fontWeight="700" letterSpacing="-0.5">
        ARIA
      </text>
      <text x="200" y="216" textAnchor="middle" fill="#9db4ff" fontFamily="inherit" fontSize="9" letterSpacing="2">
        AUTONOMY ENGINE
      </text>
      {/* nodes */}
      {[
        { x: 200, y: 50, label: 'REASON' },
        { x: 350, y: 200, label: 'ACT' },
        { x: 200, y: 350, label: 'OBSERVE' },
        { x: 50, y: 200, label: 'REPEAT' },
      ].map((n, i) => (
        <g key={n.label}>
          <motion.circle
            cx={n.x} cy={n.y} r="34" fill="#ffffff" stroke="#c4d2ec" strokeWidth="1.5"
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 + i * 0.15, duration: 0.45, ease: 'easeOut' }}
            style={{ transformOrigin: `${n.x}px ${n.y}px` }}
          />
          <text x={n.x} y={n.y + 3.5} textAnchor="middle" fill="#1a46e5" fontFamily="inherit" fontSize="10.5" fontWeight="600" letterSpacing="1">
            {n.label}
          </text>
        </g>
      ))}
    </svg>
  </div>
);

const V2Aria = () => (
  <V2Layout title="ARIA — The Autonomy Engine — Asha">
    <PageHero
      eyebrow="ARIA / Asha Reasoning & Intelligence Architecture"
      title={<>The engine that turns AI from an assistant into <span className="text-[#1a46e5]">an employee.</span></>}
      body="Most dental AI can draft, suggest, or transcribe — then it hands the work back to your team. ARIA is different: it reasons about the goal, takes real action, checks the result, and keeps going until the workflow is finished. Every Asha agent runs on it."
    >
      <CtaGhost to="/book-demo">See ARIA run a workflow</CtaGhost>
      <TextLink to="/v2/ai-team">Meet the agents it powers</TextLink>
    </PageHero>

    {/* loop */}
    <section className="px-6 py-20 md:py-24">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="01 / the loop" />
        <div className="mt-12 grid items-center gap-14 lg:grid-cols-2 lg:gap-24">
          <FadeUp>
            <LoopDiagram />
          </FadeUp>
          <Stagger className="space-y-3">
            {ARIA_LOOP.map((s, i) => (
              <StaggerItem key={s.step}>
                <div className="v2-card flex gap-5 rounded-2xl p-6">
                  <span className="v2-display text-[28px] font-semibold leading-none text-[#c4d2ec]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <div className="v2-display text-[20px] font-semibold text-[#0a1633]">{s.step}</div>
                    <p className="mt-1.5 text-[14.5px] leading-relaxed text-[#3f4d6e]">{s.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>

    {/* worked example */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <SectionHead
            dark
            eyebrow="02 / a real run"
            title={<>“Prep tomorrow's schedule — verify insurance, update charts, brief the doctor.”</>}
            body="One sentence in. Four rounds of autonomous work out. Here's what ARIA actually does with a request like that."
          />
          <div className="mt-14 space-y-px overflow-hidden rounded-2xl border border-white/12">
            {ARIA_EXAMPLE.map((r, i) => (
              <FadeUp key={r.round} delay={i * 0.08}>
                <div className="grid gap-4 bg-white/[0.04] px-6 py-6 md:grid-cols-[100px_1fr_1fr] md:gap-8 md:px-8">
                  <div>
                    <div className="v2-mono text-[11px] font-medium uppercase tracking-[0.16em] text-[#9db4ff]">round</div>
                    <div className="v2-display mt-1 text-[34px] font-semibold leading-none text-white">{r.round}</div>
                  </div>
                  <div>
                    <div className="v2-mono text-[10.5px] font-medium uppercase tracking-[0.16em] text-white/40">reasoning</div>
                    <p className="mt-2 text-[15px] leading-relaxed text-white/85">{r.reasoning}</p>
                  </div>
                  <div>
                    <div className="v2-mono text-[10.5px] font-medium uppercase tracking-[0.16em] text-white/40">
                      action — via {r.agent}
                    </div>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#9db4ff]">{r.action}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="v2-mono mt-8 text-center text-[12px] font-medium uppercase tracking-[0.16em] text-white/50">
              result: all 8 appointments prepped · chart updated · doctor briefed by sms · zero human touches
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* capabilities */}
    <section className="px-6 pb-24 md:pb-28">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="03 / under the hood" />
        <div className="mt-12">
          <SectionHead
            eyebrow="Built in-house, on dental workflows"
            title={<>Why this can't be bolted on.</>}
            body="ARIA works because it lives inside the operating system — the phones, the chart, the ledger, and the claim are all native surfaces it can act on. A wrapper around someone else's PMS will only ever watch from outside."
          />
        </div>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CAPABILITIES.map((c) => (
            <StaggerItem key={c.title} className="h-full">
              <div className="v2-card h-full rounded-2xl p-7">
                <c.icon className="h-5 w-5 text-[#1a46e5]" />
                <div className="v2-display mt-4 text-[19px] font-semibold leading-tight text-[#0a1633]">{c.title}</div>
                <p className="mt-2.5 text-[14px] leading-relaxed text-[#3f4d6e]">{c.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <FadeUp delay={0.1}>
          <div className="mt-16 grid grid-cols-2 gap-y-10 border-t border-[#dde5f3] pt-12 md:grid-cols-4">
            <BigStat value="100+" label="skills in the engine" />
            <BigStat value="20+" label="agents it powers" />
            <BigStat value="∞" label="loops until done" />
            <BigStat value="0" label="hand-backs mid-task" />
          </div>
        </FadeUp>
      </div>
    </section>

    <ClosingCta
      title="Watch ARIA finish a workflow end to end."
      body="Bring a messy real-world scenario to the demo — a stale verification, a denied claim — and watch the engine reason its way through it."
    />
  </V2Layout>
);

export default V2Aria;
